/**
 * Detección y auto-recuperación de errores de carga de chunks de Next.js.
 *
 * Durante un deploy (rollout de ECS) los chunks con hash viejo dejan de existir
 * en el contenedor nuevo. Una pestaña abierta con HTML viejo intenta cargar un
 * chunk que ya no está → `Failed to load chunk` → cae al error boundary y el
 * usuario ve la pantalla "¡Ups!", aunque el sistema está sano.
 *
 * La cura: detectar ese error puntual y recargar UNA sola vez para obtener HTML
 * fresco que apunte a los chunks nuevos. El guard en sessionStorage evita loops
 * si la recarga no resuelve (offline real, error genuino, etc.).
 */

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to load chunk|error loading dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported module/i;

const RELOAD_KEY = "rj-chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;

export function isChunkLoadError(error?: {
  name?: string;
  message?: string;
}): boolean {
  if (!error) return false;
  return CHUNK_ERROR_RE.test(`${error.name ?? ""} ${error.message ?? ""}`);
}

/**
 * Si el error es de carga de chunk, recarga la página una sola vez (con cooldown
 * para no entrar en loop). Devuelve `true` si disparó la recarga, para que el
 * caller corte el render del error boundary.
 */
export function recoverFromChunkError(error?: {
  name?: string;
  message?: string;
}): boolean {
  if (typeof window === "undefined" || !isChunkLoadError(error)) return false;

  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage no disponible (modo privado, etc.): recargamos igual una vez.
  }

  window.location.reload();
  return true;
}
